import { PaymentSnapshotPromotion, PromotionResource } from "@/interfaces";

export interface ContractPrices {
  priceSubtotal: number;
  priceBeforeDiscounts?: number;
}

export interface PriceLadder {
  /** Subtotal before ANY discount: rebates added back, promotions not yet deducted. */
  subtotal: number | null;
  /** A promotion still on the table, as a positive amount. Already-applied ones are rebates. */
  discount: number;
  /** The reduced subtotal the taxes were computed on. */
  netSubtotal: number | null;
  /** Whether a promotion row has an amount to show. */
  hasPromotion: boolean;
  /** Whether anything came off at all, so the net row says something the subtotal did not. */
  isDiscounted: boolean;
}

/**
 * The subtotal → discounts → net subtotal rungs of the order summary.
 *
 * The taxes and total the API sends are computed on the subtotal AFTER every
 * discount comes off, so a summary showing one subtotal reads as arithmetic that
 * does not add up: $100 of services, a $50 discount listed, then a $50 subtotal
 * with no explanation of which of the two the taxes were sized against.
 *
 * Discounts reach a contract two ways and the ladder has to bridge both:
 *
 *  - As rebates, which is where an ALREADY-APPLIED promotion ends up
 *    (ContractRebate carries its promotion_id). These are baked into
 *    priceSubtotal, and priceBeforeDiscounts adds them back — so they bridge
 *    subtotal → priceSubtotal, and the summary itemises them from the rebate
 *    relationship.
 *  - As a promotion still on the table — an eligible offer, or the one recorded
 *    in a payment snapshot. That bridges priceSubtotal → new_subtotal.
 *
 * The live offer is read before the snapshot on every rung. A contract can carry
 * both, and only one of them describes the taxes and total on screen, so taking
 * one rung from each is exactly what breaks the arithmetic.
 */
export const buildPriceLadder = (params: {
  offer?: PromotionResource | null;
  snapshot?: PaymentSnapshotPromotion | null;
  prices?: ContractPrices | null;
}): PriceLadder => {
  const offer = params.offer?.attributes;
  const snapshot = params.snapshot;
  const prices = params.prices;
  const hasPromotion = Boolean(offer || snapshot);

  const subtotal =
    prices?.priceBeforeDiscounts ??
    offer?.original_subtotal ??
    snapshot?.originalSubtotal ??
    prices?.priceSubtotal ??
    null;

  const discount = offer?.discount_amount ?? snapshot?.discountAmount ?? 0;

  // Prefer the reduced base the API already computed: a fixed amount sold as
  // tax-included is reconciled server-side, so subtracting here can land a cent
  // off the base the tax lines were actually built on.
  const netSubtotal =
    offer?.new_subtotal ??
    snapshot?.newSubtotal ??
    prices?.priceSubtotal ??
    subtotal;

  return {
    subtotal,
    discount,
    netSubtotal,
    hasPromotion,
    isDiscounted:
      subtotal !== null && netSubtotal !== null && netSubtotal !== subtotal,
  };
};
